import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import theme from '../../../theme';
import {
    BottomPanelButtonsWrapper,
    Curtain,
    PanelButton,
    PanelButtonsWrapper,
    PanelHeader,
    PanelSectionContent,
    PanelWrapper,
} from './OdfjellPanel.styles';
import { Position } from 'src/types/ViewModel/Stowage/Position';

export const PANEL_CONSTANTS = {
    SMALL_WIDTH: 300,
    MEDIUM_WIDTH: 500,
    LARGE_WIDTH: 700,
    VERY_LARGE_WIDTH: 1000,
    DEFAULT_WIDTH: 700,
    TOP_MARGIN: 50,
    TOP_MARGIN_ADJUSTMENT: 5,
} as const;

// Selectors for focusable elements in priority order
const FOCUSABLE_ELEMENTS = [
    '.focus-input', // First priority: elements with class "focus-input"
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled]):not([aria-hidden="true"])',
    '[tabindex]:not([tabindex="-1"]):not([disabled])',
].join(', ');

interface PanelProps {
    children: React.ReactNode;
    headerTitle?: React.ReactNode;
    extHeaderTitle?: React.ReactNode;
    panelWidth?: number;
    defaultPosition?: 'right' | 'center';
    initialPosition?: Position;
    preventOutsideClose?: boolean;
    hasCurtain?: boolean;
    onCancel?: () => void;
    onAction?: (() => Promise<void>) | (() => void);
    cancelButtonText?: string;
    showCancelButton?: boolean;
    actionButtonText?: string;
    isActionDisabled?: boolean;
    isCloseDisabled?: boolean;
    autoFocus?: boolean;
    leftButtons?: React.ReactNode;
    onPin?: () => void;
    isPinned?: boolean;
}

const OdfjellPanel: React.FC<PanelProps> = ({
    children,
    headerTitle,
    extHeaderTitle,
    panelWidth = PANEL_CONSTANTS.DEFAULT_WIDTH,
    defaultPosition = 'center',
    initialPosition,
    preventOutsideClose = false,
    hasCurtain = false,
    onCancel,
    onAction,
    showCancelButton = false,
    cancelButtonText = 'Cancel',
    actionButtonText = 'OK',
    isActionDisabled = false,
    isCloseDisabled = false,
    autoFocus = true,
    leftButtons,
    onPin,
    isPinned = false,
}) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [dragging, setDragging] = useState(false);
    const [rel, setRel] = useState<Position>({ left: 0, top: 0 });
    const [visible, setVisible] = useState(true);
    const [position, setPosition] = useState<Position | null>(null);
    const [onActionErrorMessage, setOnActionErroMessage] = useState<string | null>(null);
    const [isExecutingAction, setIsExecutingAction] = useState(false);
    const isExecutingRef = useRef(false);

    // Set initial position
    useEffect(() => {
        setPosition(getDefaultPosition());
    }, [initialPosition]);

    // Handle focus management
    useEffect(() => {
        if (!autoFocus || !contentRef.current || !visible) return;

        // Short delay to ensure DOM is fully rendered
        const focusTimeout = setTimeout(() => {
            if (!contentRef.current) return;

            // Try to find elements with focus-input class first
            const focusableElements = contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS);

            if (focusableElements && focusableElements.length > 0) {
                focusableElements[0].focus();
            }
        }, 100);

        return () => clearTimeout(focusTimeout);
    }, [visible, autoFocus, position]);

    // Set up event handlers
    useEffect(() => {
        const handleKeyDown = async (e: KeyboardEvent) => {
            // Don't trigger save action when Enter is pressed inside textarea
            const isTextareaFocused = document.activeElement instanceof HTMLTextAreaElement;

            if (e.key === 'Enter' && !isActionDisabled && !isExecutingRef.current && onAction && !isTextareaFocused) {
                await executeAction();
            } else if (e.key === 'Escape') {
                onClose();
            }
        };

        const handleMouseUp = () => setDragging(false);

        const handleClickOutside = (e: MouseEvent) => {
            if (!preventOutsideClose && panelRef.current && !panelRef.current.contains(e.target as Node)) {
                onClose();
            }
        };

        // Set up tab trap to keep focus within the panel
        const handleTabKey = (e: KeyboardEvent) => {
            if (e.key !== 'Tab' || !contentRef.current) return;

            const focusableElements = contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS);
            if (!focusableElements || focusableElements.length === 0) return;

            // Convert NodeList to array for easier handling
            const focusable = Array.from(focusableElements);
            const firstFocusable = focusable[0];
            const lastFocusable = focusable[focusable.length - 1];

            // Handle tab navigation
            if (e.shiftKey && document.activeElement === firstFocusable) {
                // If shift+tab on first element, move to last
                e.preventDefault();
                lastFocusable.focus();
            } else if (!e.shiftKey && document.activeElement === lastFocusable) {
                // If tab on last element, move to first
                e.preventDefault();
                firstFocusable.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keydown', handleTabKey);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keydown', handleTabKey);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [preventOutsideClose, isActionDisabled, isExecutingAction, onAction]);

    const getDefaultPosition = (): Position => {
        if (initialPosition) {
            return {
                left: initialPosition.left,
                top: initialPosition.top,
            };
        }

        switch (defaultPosition) {
            case 'right':
                return {
                    left: window.innerWidth - panelWidth,
                    top: theme.constants.pageHeaderHeightNumber + PANEL_CONSTANTS.TOP_MARGIN_ADJUSTMENT,
                };
            case 'center':
            default:
                return {
                    left: (window.innerWidth - panelWidth) / 2,
                    top: PANEL_CONSTANTS.TOP_MARGIN,
                };
        }
    };

    const onMouseDown = (e: React.MouseEvent): void => {
        if (e.button !== 0 || !panelRef.current) return;
        const pos = panelRef.current.getBoundingClientRect();
        setRel({
            left: e.pageX - pos.left,
            top: e.pageY - pos.top,
        });
        setDragging(true);
        e.stopPropagation();
        e.preventDefault();
    };

    const onMouseMove = (e: React.MouseEvent): void => {
        if (!dragging) return;
        setPosition({
            left: e.pageX - rel.left,
            top: e.pageY - rel.top,
        });
        e.stopPropagation();
        e.preventDefault();
    };

    const executeAction = async (): Promise<void> => {
        if (!onAction || isExecutingRef.current) return;
        isExecutingRef.current = true;
        setIsExecutingAction(true);
        try {
            const result = onAction();
            if (result instanceof Promise) {
                await result;
                if (onActionErrorMessage) {
                    setOnActionErroMessage(null);
                }
            }
        } catch (error) {
            console.error('Action execution failed:', error);
            setOnActionErroMessage(error instanceof Error ? error.message : String(error));
        } finally {
            isExecutingRef.current = false;
            setIsExecutingAction(false);
        }
    };

    const getMaxContentHeightStyle = (): React.CSSProperties => {
        const HEADER_HEIGHT = 40;
        const BUTTONS_SECTION_HEIGHT = 60;
        const SAFETY_MARGIN = 20;

        const availableHeight = window.innerHeight - PANEL_CONSTANTS.TOP_MARGIN - HEADER_HEIGHT - BUTTONS_SECTION_HEIGHT - SAFETY_MARGIN;

        // Always apply maxHeight + overflow so the scrollbar appears immediately when content
        // grows (e.g. when "Show Stowage Items" is toggled). The previous approach read
        // contentRef.current.scrollHeight during render, which returns the stale pre-update
        // height and therefore missed the overflow condition until a subsequent re-render.
        return {
            maxHeight: `${availableHeight}px`,
            overflow: 'hidden auto',
        };
    };

    const onClose = (): void => {
        setVisible(false);
        // Always call onCancel if it exists when closing the panel
        if (onCancel) {
            onCancel();
        }
    };

    if (!visible || !position) return null;

    return (
        <>
            {hasCurtain && <Curtain />}
            <PanelWrapper
                ref={panelRef}
                style={{
                    left: `${position.left}px`,
                    top: `${position.top}px`,
                    width: `${panelWidth}px`,
                    position: 'fixed',
                }}
                defaultPosition={defaultPosition}
                onMouseMove={onMouseMove}
            >
                <PanelHeader className="header" onMouseDown={onMouseDown}>
                    <span className="header-content">{headerTitle}</span>
                    <span className="ext-header">{extHeaderTitle && <span className="ext-info">{extHeaderTitle}</span>}</span>
                    <span className="header-actions">
                        {onPin && (
                            <span
                                className="minimize-icon"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onPin();
                                }}
                                title={isPinned ? 'Unpin panel' : 'Minimize panel'}
                            >
                                <FontAwesomeIcon icon="down-left-and-up-right-to-center" />
                            </span>
                        )}
                        {!isCloseDisabled && (
                            <span
                                className="close-icon"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={() => onClose()}
                                title="Close"
                            >
                                <FontAwesomeIcon icon="times" />
                            </span>
                        )}
                    </span>
                </PanelHeader>
                <PanelSectionContent className="panel-content" ref={contentRef} style={getMaxContentHeightStyle()}>
                    {children}
                </PanelSectionContent>
                <PanelButtonsWrapper className="panel-content">
                    {onActionErrorMessage && <div className="error-message">{onActionErrorMessage}</div>}
                </PanelButtonsWrapper>
                <BottomPanelButtonsWrapper>
                    <div className="left-buttons">{leftButtons}</div>
                    <div className="right-buttons">
                        {showCancelButton && (
                            <div className="button-bar">
                                <PanelButton onClick={() => onClose()}>{cancelButtonText}</PanelButton>
                            </div>
                        )}
                        {onAction && (
                            <div className="button-bar">
                                <PanelButton onClick={executeAction} disabled={isActionDisabled || isExecutingAction}>
                                    {actionButtonText}
                                </PanelButton>
                            </div>
                        )}
                    </div>
                </BottomPanelButtonsWrapper>
            </PanelWrapper>
        </>
    );
};

export default OdfjellPanel;
